/**
 * Resolving a field's starting value for the person opening the form (C10).
 *
 * Every requester retypes their own department, site and job title onto every
 * case. The system already knows all three (A2), and a form that starts empty
 * is asking somebody to copy out their own staff record.
 *
 * **Resolved here, in the client, and not baked into the schema.** A profile
 * default has no value until a particular person opens the form: writing one
 * into the stored schema would freeze whoever last edited the template into it,
 * and every requester would then start with that administrator's department.
 * `schema.applyDefault` writes a plain Formily `default` only for a *fixed*
 * value, which is the one kind that is the same for everybody.
 *
 * **A default never overwrites an answer.** It fills a field that has no stored
 * value, on a case that has not been filled in yet. Anything else would rewrite
 * somebody's work every time they reopened a section, which is the opposite of
 * helpful.
 */

/** The shape `x-designer-default` is stored in. Mirrors `schema.FieldDefault`. */
type StoredDefault =
  | { source: "fixed"; value: any }
  | { source: "profile"; key: string }
  | { source: "today" };

/** What the signed-in person's record can supply. */
export type Profile = {
  name?: string;
  email?: string;
  department?: string;
  site?: string;
  job_title?: string;
  company?: string;
  employee_id?: string;
};

const today = () => new Date().toISOString().slice(0, 10);

const resolve = (spec: StoredDefault, profile: Profile): any => {
  if (spec.source === "fixed") return spec.value;
  if (spec.source === "today") return today();
  if (spec.source === "profile") {
    const value = (profile as any)?.[spec.key];
    // An absent value stays absent rather than becoming the empty string: a
    // field the requester still has to fill in should look unfilled, and ""
    // satisfies a `required` check on some components.
    return value === undefined || value === null || value === "" ? undefined : value;
  }
  return undefined;
};

/**
 * Walk a stored schema and collect `{ fieldName: startingValue }`.
 *
 * Reads `x-designer-default` — the structured copy — rather than Formily's
 * `default`, for the reason the whole rules layer exists: the generated key
 * cannot say *why* it holds that value, and "the person's own site" is not
 * expressible as one.
 */
export const collectDefaults = (
  schemaJson: any,
  profile: Profile
): Record<string, any> => {
  const out: Record<string, any> = {};

  const walk = (node: any) => {
    if (!node || typeof node !== "object") return;
    const properties = node.properties;
    if (properties && typeof properties === "object") {
      Object.entries<any>(properties).forEach(([key, child]) => {
        if (!child || typeof child !== "object") return;
        const spec = child["x-designer-default"];
        const name = child.name || key;
        if (spec && spec.source) {
          const value = resolve(spec as StoredDefault, profile);
          if (value !== undefined) out[name] = value;
        }
        walk(child);
      });
    }
    // A repeating table's columns are one level down. A default inside one
    // would apply per row, which nothing asks for yet -- walked so the shape is
    // understood rather than silently skipped.
    if (node.items) walk(node.items);
  };

  walk(schemaJson?.schema ?? schemaJson);
  return out;
};

/**
 * The stored values, with defaults filling only what is genuinely absent.
 *
 * `values` wins over every default, always. The check is for `undefined`/`null`
 * /`""` rather than falsiness: `0` and `false` are real answers somebody gave,
 * and treating them as empty would overwrite a zero reading with a default on
 * every reopen.
 */
export const withDefaults = (
  values: Record<string, any> | undefined,
  defaults: Record<string, any>
): Record<string, any> => {
  const out: Record<string, any> = { ...(values ?? {}) };
  Object.entries(defaults).forEach(([key, value]) => {
    const current = out[key];
    if (current === undefined || current === null || current === "") {
      out[key] = value;
    }
  });
  return out;
};

/** The signed-in person, from the profile the app already keeps in storage. */
export const currentProfile = (): Profile => {
  try {
    const raw = localStorage.getItem("userInfo");
    const info = raw ? JSON.parse(raw) : {};
    return {
      name: info?.name,
      email: info?.email,
      department: info?.department ?? info?.org_unit_name,
      site: info?.site,
      job_title: info?.job_title,
      company: info?.company,
      employee_id: info?.employee_id,
    };
  } catch {
    // A default is a convenience; it must never be the reason a form fails to
    // open.
    return {};
  }
};
